import { useEffect, useMemo, useRef, useState } from 'react';
import { MESSAGE_TYPE_ERROR } from '../../../chat/constants';
import { QUICK_CHAT_PREVIEW_FADE_OUT_MS, QUICK_CHAT_PREVIEW_MAX_COUNT, QUICK_CHAT_PREVIEW_TEXT_MAX_LENGTH, QUICK_CHAT_PREVIEW_TTL_MS } from '../../../meeting-ui/constants';
/**
 * 截断聊天小窗预览正文.
 *
 * @param {string} text - 原始正文.
 * @returns {string}
 */
export function truncatePreviewText(text) {
    if (text.length <= QUICK_CHAT_PREVIEW_TEXT_MAX_LENGTH) {
        return text;
    }
    return `${text.slice(0, QUICK_CHAT_PREVIEW_TEXT_MAX_LENGTH)}…`;
}
/**
 * 判断消息是否仍在预览有效期内.
 *
 * @param {IMessage} message - 聊天消息.
 * @param {number} nowMs - 当前时间戳（毫秒）.
 * @returns {boolean}
 */
function isPreviewMessageActive(message, nowMs) {
    if (typeof message.timestamp !== 'number') {
        return false;
    }
    return nowMs - message.timestamp <= QUICK_CHAT_PREVIEW_TTL_MS;
}
/**
 * 筛选适合在聊天小窗展示的公开消息（最多 N 条，且未过期）.
 *
 * @param {IMessage[]} messages - 全部聊天消息.
 * @param {number} nowMs - 当前时间戳（毫秒）.
 * @returns {IMessage[]}
 */
function getQuickChatPreviewMessages(messages, nowMs) {
    return messages
        .filter(message => !message.privateMessage
        && !message.isReaction
        && !message.lobbyChat
        && message.messageType !== MESSAGE_TYPE_ERROR
        && Boolean(message.message?.trim())
        && isPreviewMessageActive(message, nowMs))
        .slice(-QUICK_CHAT_PREVIEW_MAX_COUNT);
}
/**
 * 将活跃预览与上一帧渲染列表合并：离开活跃集的气泡标记为 fading.
 *
 * @param {IPreviewRenderItem[]} previous - 上一帧渲染项.
 * @param {IMessage[]} activeMessages - 仍在 TTL 内的消息.
 * @returns {IPreviewRenderItem[]}
 */
function mergePreviewRenderItems(previous, activeMessages) {
    const activeById = new Map(activeMessages.map(message => [message.messageId, message]));
    const next = [];
    const seenIds = new Set();
    for (const item of previous) {
        const messageId = item.message.messageId;
        const activeMessage = activeById.get(messageId);
        if (activeMessage) {
            next.push({
                fading: false,
                message: activeMessage
            });
            seenIds.add(messageId);
        }
        else {
            next.push({
                fading: true,
                message: item.message
            });
            seenIds.add(messageId);
        }
    }
    for (const message of activeMessages) {
        if (!seenIds.has(message.messageId)) {
            next.push({
                fading: false,
                message
            });
        }
    }
    return next;
}
/**
 * 快捷聊天预览气泡状态：TTL、淡出、到期刷新.
 *
 * @param {Array} messages - 聊天消息列表.
 * @returns {Object} Preview tick helper and rendered preview items.
 */
export function useQuickChatPreviews(messages) {
    const fadeOutTimersRef = useRef(new Map());
    const [previewTick, setPreviewTick] = useState(0);
    const [renderedPreviews, setRenderedPreviews] = useState([]);
    const activePreviewMessages = useMemo(() => {
        void previewTick;
        return getQuickChatPreviewMessages(messages, Date.now());
    }, [messages, previewTick]);
    useEffect(() => {
        setRenderedPreviews(prev => mergePreviewRenderItems(prev, activePreviewMessages));
    }, [activePreviewMessages]);
    useEffect(() => {
        const fadingIds = new Set(renderedPreviews.filter(item => item.fading).map(item => item.message.messageId));
        const activeIds = new Set(renderedPreviews.filter(item => !item.fading).map(item => item.message.messageId));
        for (const messageId of activeIds) {
            const pendingTimer = fadeOutTimersRef.current.get(messageId);
            if (typeof pendingTimer === 'number') {
                window.clearTimeout(pendingTimer);
                fadeOutTimersRef.current.delete(messageId);
            }
        }
        for (const messageId of fadingIds) {
            if (fadeOutTimersRef.current.has(messageId)) {
                continue;
            }
            const timerId = window.setTimeout(() => {
                fadeOutTimersRef.current.delete(messageId);
                setRenderedPreviews(current => current.filter(entry => entry.message.messageId !== messageId));
            }, QUICK_CHAT_PREVIEW_FADE_OUT_MS);
            fadeOutTimersRef.current.set(messageId, timerId);
        }
    }, [renderedPreviews]);
    useEffect(() => {
        if (activePreviewMessages.length === 0) {
            return;
        }
        const earliestExpiry = Math.min(...activePreviewMessages.map(message => message.timestamp + QUICK_CHAT_PREVIEW_TTL_MS));
        const delayMs = Math.max(0, earliestExpiry - Date.now());
        const timerId = window.setTimeout(() => {
            setPreviewTick(tick => tick + 1);
        }, delayMs);
        return () => {
            window.clearTimeout(timerId);
        };
    }, [activePreviewMessages]);
    useEffect(() => () => {
        for (const timerId of fadeOutTimersRef.current.values()) {
            window.clearTimeout(timerId);
        }
        fadeOutTimersRef.current.clear();
    }, []);
    return {
        bumpPreviewTick: () => setPreviewTick(tick => tick + 1),
        renderedPreviews
    };
}
